import { useMemo, useState } from "react";
import { useHotelGuestCount } from "@apac/hooks";
import {
  type GuestPickerComponentProps,
  useGuestPicker as useCoreGuestPicker,
} from "@tenant/hotels";

export const MAX_HOTEL_GUESTS = 16;
export const MAX_CHILD_AGE = 17;

export const useGuestPickerWithInfants = (props: GuestPickerComponentProps) => {
  const {
    guests: { adults, children },
  } = props;
  const { context, handlers, text } = useCoreGuestPicker(props);

  const initialCounts = useMemo(() => {
    const counts = { infantCount: 0, childrenCount: 0 };
    for (const num of children) {
      if (num <= 1) {
        counts.infantCount++;
      } else {
        counts.childrenCount++;
      }
    }
    return counts;
  }, [children]);

  const [infantCount, setInfantCount] = useState<number>(
    initialCounts.infantCount
  );
  const [childrenCount, setChildrenCount] = useState<number>(
    initialCounts.childrenCount
  );

  const guestCountString = useHotelGuestCount({
    adultsCount: context.adultCount,
    childrenCount,
    infantCount,
  }).formattedGuestCountString(null);

  const maximumReached = useMemo(
    () => context.adultCount + infantCount + childrenCount >= MAX_HOTEL_GUESTS,
    [context.adultCount, infantCount, childrenCount]
  );

  const getChildrenArray = () => {
    const childrenArray = Array.from(
      { length: childrenCount },
      (_, i) => context.childAgeArr[i] ?? MAX_CHILD_AGE
    );
    const infantArray = Array(infantCount).fill(1);
    return [...childrenArray, ...infantArray];
  };

  const resetGuestsToValidState = () => {
    handlers.onAdultChange(adults);
    setInfantCount(initialCounts.infantCount);
    setChildrenCount(initialCounts.childrenCount);
  };

  const onSubmit = () =>
    handlers.onGuestSelection({
      adults: context.adultCount,
      children: getChildrenArray(),
    });

  return {
    text: {
      ...text,
      buttonLabel: guestCountString,
    },
    context: {
      ...context,
      infantCount,
      childrenCount,
      maximumReached,
      isGuestPickerValid: true,
    },

    handlers: {
      ...handlers,
      onSubmit,
      setInfantCount,
      setChildrenCount,
      getChildrenArray,
      resetGuestsToValidState,
    },
  };
};
